// Signup flow: email + password → verify email → MFA enrolment → write user doc → home.
//
// Why three stages on one page rather than separate routes: the auth user
// is created in stage 1 and stays the same identity through to stage 3.
// Routing away (and possibly remounting) risks losing the in-memory user
// object before enrolment completes. The page reads `authUser` from the
// auth context so the email-verification round-trip (user clicks link in
// email → comes back here) resumes cleanly without local state.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, Spline } from 'lucide-react';
import {
  createUserWithEmailAndPassword,
  multiFactor,
  sendEmailVerification,
  signOut,
  type User,
} from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { authErrorMessage } from '../lib/auth-errors';
import { createUserProfile } from '../lib/users';
import MfaEnrollmentStep from '../components/auth/MfaEnrollmentStep';
import {
  AuthShell,
  Badge,
  Button,
  FormField,
  Input,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

type Stage = 'collect-credentials' | 'verify-email' | 'enrol-mfa' | 'finishing';

export default function SignupPage() {
  const [stage, setStage] = useState<Stage>('collect-credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading } = useAuth();

  // Resume mid-signup: someone landed on /signup with a partially completed
  // account (closed the tab, clicked the verification link in another tab,
  // hit a profile-write failure last time, etc.). Without this, the page
  // would render the credentials form and the next submit would trip
  // email-already-in-use.
  useEffect(() => {
    if (authLoading) return;
    if (!authUser) return;
    if (user) return;

    setUser(authUser);
    if (!authUser.emailVerified) {
      setStage('verify-email');
    } else if (multiFactor(authUser).enrolledFactors.length === 0) {
      setStage('enrol-mfa');
    } else {
      setStage('finishing');
    }
  }, [authUser, authLoading, user, navigate]);

  // Auto-detect email verification when the user returns to this tab.
  useEffect(() => {
    if (stage !== 'verify-email') return;
    const recheck = async () => {
      if (!auth.currentUser) return;
      try {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          setUser(auth.currentUser);
          setStage('enrol-mfa');
        }
      } catch (err) {
        console.error('[signup] reload during verify-email failed', err);
      }
    };
    window.addEventListener('focus', recheck);
    return () => window.removeEventListener('focus', recheck);
  }, [stage]);

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const { user: created } = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password,
      );
      setUser(created);
      await sendEmailVerification(created, { url: `${window.location.origin}/signup` });
      setStage('verify-email');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    if (!user || busy) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await sendEmailVerification(user, { url: `${window.location.origin}/signup` });
      setInfo('Verification email sent. Check your inbox (and spam folder).');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function manualRecheck() {
    if (!user || busy) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await user.reload();
      if (auth.currentUser?.emailVerified) {
        setUser(auth.currentUser);
        setStage('enrol-mfa');
      } else {
        setInfo('Still unverified. Click the link in your email and try again.');
      }
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // The 'finishing' stage drives the profile write — single retry surface
  // for "just enrolled" and "resume after a failed write".
  useEffect(() => {
    if (stage !== 'finishing') return;
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        await createUserProfile({
          uid: user.uid,
          email: user.email ?? email.trim(),
          name: name.trim() || undefined,
          mfaEnrolled: true,
        });
        if (!cancelled) navigate('/', { replace: true });
      } catch (err) {
        console.error('[signup] profile save failed', err);
        if (!cancelled) {
          setError(`Couldn't save your profile: ${firestoreErrorMessage(err)}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stage, user, email, name, navigate]);

  function onMfaEnrolled() {
    setStage('finishing');
  }

  async function retryProfileSave() {
    if (!user || busy) return;
    setError(null);
    setBusy(true);
    try {
      await createUserProfile({
        uid: user.uid,
        email: user.email ?? email.trim(),
        name: name.trim() || undefined,
        mfaEnrolled: true,
      });
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[signup] profile save retry failed', err);
      setError(`Couldn't save your profile: ${firestoreErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  function abort() {
    signOut(auth).catch(() => undefined);
    setUser(null);
    setStage('collect-credentials');
  }

  return (
    <AuthShell>
      <Stack gap={6}>
        {stage === 'collect-credentials' && (
          <>
            <Stack gap={1}>
              <h1 style={{ fontSize: 'var(--fs-2xl)' }}>Create your account</h1>
              <p className="ds-text-muted ds-text-sm">
                Two-factor auth is required — you&apos;ll set it up next.
              </p>
            </Stack>
            {error && <ErrorBanner message={error} />}
            <Stack gap={4} as="form" onSubmit={submitCredentials}>
              <FormField label="Name">
                {(id) => (
                  <Input
                    id={id}
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Marc Jolly"
                  />
                )}
              </FormField>
              <FormField label="Email">
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                  />
                )}
              </FormField>
              <FormField label="Password" hint="Minimum 6 characters">
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                )}
              </FormField>
              <Button
                type="submit"
                variant="primary"
                block
                loading={busy}
                iconRight={!busy ? <ArrowRight size={14} strokeWidth={2} /> : undefined}
              >
                Create account
              </Button>
            </Stack>
            <p className="ds-text-muted ds-text-sm ds-text-center">
              Already have an account? <Link to="/signin">Sign in</Link>.
            </p>
          </>
        )}

        {stage === 'verify-email' && user && (
          <Stack gap={4}>
            <Stack gap={2}>
              <Row gap={2} align="center">
                <Mail size={20} strokeWidth={1.75} color="var(--color-accent)" />
                <h2 style={{ fontSize: 'var(--fs-xl)' }}>Verify your email</h2>
              </Row>
              <p className="ds-text-muted ds-text-sm">
                We sent a verification link to <strong className="ds-mono">{user.email}</strong>.
                Click it, then come back to this tab — we&apos;ll detect it automatically.
              </p>
            </Stack>
            {info && (
              <div
                style={{
                  background: 'var(--color-accent-soft)',
                  border: '1px solid rgba(124, 92, 255, 0.2)',
                  color: 'var(--color-accent-soft-text)',
                  padding: 'var(--space-3) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--fs-sm)',
                }}
              >
                {info}
              </div>
            )}
            {error && <ErrorBanner message={error} />}
            <Stack gap={2}>
              <Button variant="primary" block onClick={manualRecheck} loading={busy}>
                I&apos;ve verified — continue
              </Button>
              <Button variant="secondary" block onClick={resendVerification} disabled={busy}>
                Resend verification email
              </Button>
              <Button variant="ghost" block onClick={abort} disabled={busy}>
                Cancel and start over
              </Button>
            </Stack>
          </Stack>
        )}

        {stage === 'enrol-mfa' && user && (
          <Stack gap={4}>
            <Row gap={2} align="center">
              <Spline size={20} strokeWidth={1.75} color="var(--color-accent)" />
              <h2 style={{ fontSize: 'var(--fs-xl)' }}>Set up two-factor auth</h2>
              <Badge tone="warning" dot>
                Required
              </Badge>
            </Row>
            {error && <ErrorBanner message={error} />}
            <MfaEnrollmentStep user={user} onEnrolled={onMfaEnrolled} />
            <Button variant="ghost" size="sm" onClick={abort}>
              Cancel and start over
            </Button>
          </Stack>
        )}

        {stage === 'finishing' &&
          (error ? (
            <Stack gap={3}>
              <ErrorBanner message={error} />
              <p className="ds-text-muted ds-text-sm">
                Your account and 2FA are set up. We just need to save your profile.
              </p>
              <Button variant="primary" onClick={retryProfileSave} loading={busy}>
                Retry
              </Button>
            </Stack>
          ) : (
            <Row gap={2} align="center" justify="center">
              <Spinner size="md" />
              <span className="ds-text-muted">Finishing up…</span>
            </Row>
          ))}
      </Stack>
    </AuthShell>
  );
}

function firestoreErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: unknown }).code;
    switch (code) {
      case 'permission-denied':
        return 'Permission denied by Firestore rules. The rules may not be deployed yet.';
      case 'unavailable':
        return 'Firestore is unavailable. Check your network and try again.';
      case 'unauthenticated':
        return 'Your session expired. Sign out and back in, then try again.';
    }
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Something went wrong. Try again.';
}
